import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MasterDataType, UserDataType } from "../types/dataTypes";
import {
  useGetCampaignContactableDetailsQuery,
  useGetCampaignDraftDetailsQuery,
  useGetCampaignFaqDetailsQuery,
  useGetCampaignInitialDetailsQuery,
} from "../redux/services/whatsAppCampaignService";
import {
  useGetCampaignSenderDetailsQuery,
  useGetWhatsAppTemplatesQuery,
} from "../redux/services/campaignComposeService";
import { useGetLocationDataQuery } from "../redux/services/locationService";
import { campaignComposeActions } from "../redux/slice/campaignComposeSlice";
import { campaignSetupActions } from "../redux/slice/campaignSetUpSlice";
import { whatsAppCampaignSliceActions } from "../redux/slice/whatsAppCampaignSlice";
import {
  EMediaType,
  ICampaignInitialDetails,
  IJobLocations,
  ILocationResponse,
} from "../types/campaign.types";
import {
  formatNumberString,
  getIsFresher,
} from "../utils/whatsAppUtils";
import { useGetFoldersQuery } from "../redux/services/campaignSetupService";
import { ChannelInfo, getChannelId, getCurrencyBySiteContext } from "../utils/helpers";
import { setCurrency, setDisabledField, setMasterData } from "../redux/slice/campaignSlice";
import { campaignMaxLimitSelector } from "../redux/selectors/user.selector";

interface IProps {
  campaignId: string | null;
  initiationId: string | null;
  userData: UserDataType;
  profileCount: number | null;
  masterData: MasterDataType
}

const getJobLocationsMap = (
  jobLocationsData: ILocationResponse
): { [locationName: string]: IJobLocations } => {
  const jobLocationsMap: { [locationName: string]: IJobLocations } = {};
  jobLocationsData["topCities"]?.forEach((item) => {
    jobLocationsMap[item.value] = {
      id: `${item.id}`,
      name: item.value,
      parent: "topCities",
    };
  });

  jobLocationsData["internationalLocations"]?.forEach((item) => {
    jobLocationsMap[item.value] = {
      id: `${item.id}`,
      name: item.value,
      parent: "internationalLocations",
    };
  });

  jobLocationsData["regionAndStates"]?.forEach((regionAndState) => {
    regionAndState?.cities?.forEach((city) => {
      jobLocationsMap[city.value] = {
        id: `${city.id}`,
        name: city.value,
        parent: `${regionAndState.id}`,
      };
    });
  });

  return jobLocationsMap;
};

const useCampaignApis = ({
  campaignId,
  initiationId,
  userData,
  profileCount,
  masterData
}: IProps) => {
  const dispatch = useDispatch();
  const campaignMaxLimit = useSelector(campaignMaxLimitSelector);


  // api call for initiation campaign details
  const { data: initialData, isSuccess: isInitialDataSuccess } =
    useGetCampaignInitialDetailsQuery(
      {
        initiationId: initiationId || "",
      },
      { skip: !!campaignId || !initiationId }
    );

  // api call to get saved/draft campaign details
  const {
    data: campaignDraftData,
    isSuccess: isCampaignDraftSuccess,
    isError: isCampaignDraftError,
  } = useGetCampaignDraftDetailsQuery(
    {
      campaignId: campaignId || "",
    },
    { skip: !campaignId }
  );

  // api call for campaign contactable details
  const { data: contactableData, isSuccess: isContactableApiSuccess } =
    useGetCampaignContactableDetailsQuery(
      {
        initiationId: initiationId || "",
      },
      { skip: !initiationId }
    );

  // api call for faq details
  const { data: faqData, isSuccess: isFaqSuccess } =
    useGetCampaignFaqDetailsQuery({});

  // api call to fetch job locations
  const { data: jobLocationsData, isSuccess: isJobLocationsSuccess } =
    useGetLocationDataQuery({ userData: masterData }, { skip: !userData?.username });

  // api call to fetch the templates
  const {
    data: templates,
    isSuccess: isTemplatesSuccess,
    isLoading: isTemplatesLoading,
    isError: isTemplatesError,
  } = useGetWhatsAppTemplatesQuery({});

  // api call to get sender details
  const { data: senderDetails, isSuccess: isSenderDetailsSuccess } =
    useGetCampaignSenderDetailsQuery({});

  // api call to fetch folders
  const {
    data: folders,
    isSuccess: isFoldersSuccess,
    isError: isFoldersError,
    isLoading: isFoldersLoading,
  } = useGetFoldersQuery({});

  const getExp = (
    data: ICampaignInitialDetails,
    value?: number | string | null,
    isMax?: boolean
  ) => {
    if (
      (typeof data?.maxExp === "number" || typeof data?.maxExp === "string") &&
      (typeof data?.minExp === "number" || typeof data?.minExp === "string") &&
      +data?.maxExp === 0 &&
      +data?.minExp === 0
    ) {
      if (isMax) {
        return "";
      }
      return "Fresher";
    }

    if (isMax && (value === 0 || value === "0")) {
      return "";
    }
    if (typeof value === "string") {
      return value;
    }

    if (value === 0 || value === 1) {
      return `${value} year`;
    }

    if (value) {
      return `${value} years`;
    }

    return null;
  };

  useEffect(() => {
    // update redux state for initial details
     if(initialData || campaignDraftData?.campaignJobDescription?.disabledFields || campaignDraftData?.campaignJobDescription?.channelName){
      
       const { maxExp, minExp, jobLocations } = initialData || {};
       const srpDisabledFields = campaignDraftData?.campaignJobDescription.disabledFields
       const channelName = campaignDraftData?.campaignJobDescription?.channelName 
       if(channelName){
       const { channelId } : ChannelInfo = getChannelId(channelName)
       const subChannelId = Number(channelId) >= (channelId ?? +userData?.channelId ) ? Number(userData?.subChannelId): channelId
       const masterDataQuery = {
                       channelId:channelId || userData?.channelId,
                       subChannelId: subChannelId || userData?.subChannelId,
                       recruiterId: userData?.recruiterId,
                     }
        dispatch(setMasterData({masterData: masterDataQuery }))
       }
       
      dispatch(setDisabledField({
           minExp: minExp ?? srpDisabledFields?.includes("minExp") ?? false,
           maxExp: maxExp ?? srpDisabledFields?.includes("maxExp") ?? false,
           jobLocations: jobLocations ?? srpDisabledFields?.includes("jobLocations") ?? false,
        }));
      }

    if (isInitialDataSuccess && initialData) {
      dispatch(
        whatsAppCampaignSliceActions.setCampaignInitialDetails({
          campaignInitialDetails: initialData,
        })
      );

      // default currency
      const siteContext = userData?.siteContext;
      const { minSalary, maxSalary, jobTitle, maxExp, minExp, currency } = initialData;
      
     

      
      const defaultCurrency = currency || getCurrencyBySiteContext(siteContext);
      dispatch(setCurrency({currency:defaultCurrency}))
      const isIndianCurrency =  defaultCurrency === "INR" 

      const isMinSalaryGreaterThanLimit = minSalary && minSalary >= 10000000;
      const isMaxSalaryGreaterThanLimit = maxSalary && maxSalary >= 10000000;

      // update the campaign setup details using initial details
      dispatch(
        campaignSetupActions.setCampaignSetupDetails({
          campaignDetails: {
            jobTitle: jobTitle,
            maxExp: getExp(initialData, maxExp, true),
            minExp: getExp(initialData, minExp),
            minSalary: formatNumberString({
              numStr: isMinSalaryGreaterThanLimit && isIndianCurrency
                ? `${"10000000"}`
                : `${minSalary || ""}`,
              isIndianCurrency,
            }),
            maxSalary: formatNumberString({
              numStr: isMaxSalaryGreaterThanLimit && isIndianCurrency
                ? `${"10000000+"}`
                : `${maxSalary || ""}`,
              isIndianCurrency,
            }),
            currency
          },
        })
      );
    }
  }, [initialData, dispatch, isInitialDataSuccess, userData?.siteContext, userData, campaignDraftData]);

  useEffect(() => {
    // update redux state for contactable details
    if (contactableData && isContactableApiSuccess) {
      dispatch(
        whatsAppCampaignSliceActions.setCampaignContactableDetails({
          contactableDetails: {
            ...contactableData,
            profileCount: Math.min(
              profileCount || contactableData?.totalCount || 0,
              +campaignMaxLimit
            ),
          },
        })
      );

      if (contactableData?.contactableCount) {
        dispatch(
          campaignSetupActions.setNumberOfSelectedCandidates({
            numberOfSelectedCandidates: `${contactableData.contactableCount}`,
          })
        );
      }
    }
  }, [contactableData, dispatch, isContactableApiSuccess, profileCount, campaignMaxLimit]);

  useEffect(() => {
    if (campaignId && campaignDraftData && isCampaignDraftSuccess) {
      // update redux state for draft/saved details
      dispatch(
        whatsAppCampaignSliceActions.setCampaignDraftDetails({
          draftDetails: campaignDraftData,
        })
      );

      const maxExp = campaignDraftData.campaignJobDescription?.maxExp;
      const minExp = campaignDraftData.campaignJobDescription?.minExp;

      const maxSalary = campaignDraftData.campaignJobDescription?.maxSalary;
      const minSalary = campaignDraftData.campaignJobDescription?.minSalary;

      // update campaign setup details using draft/saved details
      dispatch(
        campaignSetupActions.setCampaignSetupDetails({
          campaignDetails: {
            jobTitle: campaignDraftData.campaignJobDescription?.jobTitle || "",
            maxExp: getIsFresher(minExp) ? null : maxExp || null,
            minExp: minExp || null,
            minSalary: minSalary ? `${minSalary}` : "",
            maxSalary: maxSalary ? `${maxSalary}` : "",
            campaignName: campaignDraftData.campaignDetail?.name,
            currency: campaignDraftData.campaignJobDescription?.currencyCode,
            isSendToAlreadyContacted:
              typeof campaignDraftData?.campaignDetail
                ?.sendMailToContactedProfiles === "number"
                ? !!campaignDraftData.campaignDetail
                    ?.sendMailToContactedProfiles
                : undefined,
            selectedCandidates:
              campaignDraftData.campaignDetail?.selectedCandidatesCount,
            selectedFolderId: campaignDraftData.campaignDetail?.folderId,
            jobMode: campaignDraftData.campaignJobDescription?.jobMode,
            noticePeriod:
              campaignDraftData.campaignJobDescription?.noticePeriod,
          },
        })
      );

      // update campaign compose details using draft/saved details
      // dispatch(
      //   campaignComposeActions.setDetailsFromDraft({
      //     mediaFileName:
      //       campaignDraftData.campaignJobDescription?.mediaFileName || "",
      //     mediaFileSize:
      //       campaignDraftData.campaignJobDescription?.mediaFileSize || "",
      //     mediaType:
      //       campaignDraftData.campaignJobDescription?.mediaFileType || "",
      //     uploadedFile:
      //       campaignDraftData.campaignJobDescription?.mediaFile || "",
      //   })
      // );
    }
  }, [campaignDraftData, isCampaignDraftSuccess, campaignId, dispatch]);

  useEffect(() => {
    // update redux state for faq details
    if (faqData && isFaqSuccess) {
      dispatch(
        whatsAppCampaignSliceActions.setCampaignFaqDetails({
          faqDetails: faqData,
        })
      );
    }
  }, [
    campaignDraftData,
    isCampaignDraftSuccess,
    campaignId,
    dispatch,
    faqData,
    isFaqSuccess,
  ]);

  useEffect(() => {
    if (jobLocationsData && isJobLocationsSuccess) {
      // update redux state for job locations details
      dispatch(
        whatsAppCampaignSliceActions.setJobLocationsDetails({
          jobLocations: jobLocationsData,
        })
      );

      // sort currency list to show india at top
      const sortedCurrencyList = [...jobLocationsData.currency].sort((a, b) =>
        a.id === "INR" ? -1 : b.id === "INR" ? 1 : 0
      );

      // updating currency list by adding code field
      const currency =
        sortedCurrencyList?.map((item) => {
          return { ...item, code: item.id?.toLowerCase()?.slice(0, 2) };
        }) || [];

      dispatch(
        whatsAppCampaignSliceActions.setCurrencyList({
          currency: currency,
        })
      );

      // update job location in campaign setup using initial details
      const jobLocationsMap: { [locationName: string]: IJobLocations } =
        getJobLocationsMap(jobLocationsData);
      const jobLocations: IJobLocations[] =
        initialData?.jobLocations?.map((item) => jobLocationsMap[item]) || [];

      dispatch(
        campaignSetupActions.setJobLocations({
          jobLocations:
            jobLocations?.length > 5 ? jobLocations?.slice(0, 5) : jobLocations,
        })
      );
    }
  }, [
    campaignDraftData,
    isCampaignDraftSuccess,
    campaignId,
    dispatch,
    faqData,
    isFaqSuccess,
    jobLocationsData,
    isJobLocationsSuccess,
    initialData?.jobLocations,
  ]);

  useEffect(() => {
    // update templates
    if (isTemplatesSuccess) {
      dispatch(
        campaignComposeActions.setTemplates({
          templates: templates?.length ? templates : [],
        })
      );
      dispatch(
        campaignComposeActions.setTemplateFetchStatus({ status: "success" })
      );
    }

    if (isTemplatesLoading) {
      dispatch(
        campaignComposeActions.setTemplateFetchStatus({ status: "pending" })
      );
    }

    if (isTemplatesError) {
      dispatch(
        campaignComposeActions.setTemplateFetchStatus({ status: "error" })
      );
    }
  }, [
    dispatch,
    isTemplatesError,
    isTemplatesLoading,
    isTemplatesSuccess,
    templates,
  ]);

  useEffect(() => {
    // update jobLocation using draft details
    if (
      campaignDraftData &&
      isCampaignDraftSuccess &&
      jobLocationsData &&
      isJobLocationsSuccess
    ) {
      const jobLocationsMap: { [locationName: string]: IJobLocations } =
        getJobLocationsMap(jobLocationsData);
      const jobLocations: IJobLocations[] =
        campaignDraftData?.campaignJobDescription?.jobLocations
          ?.map((item) => jobLocationsMap[item])
          ?.filter((item) => !!item) || [];

      dispatch(campaignSetupActions.setJobLocations({ jobLocations }));
    }
  }, [
    campaignDraftData,
    dispatch,
    initialData?.jobLocations,
    isCampaignDraftSuccess,
    isJobLocationsSuccess,
    jobLocationsData,
  ]);

  useEffect(() => {
    // updated selected template using draft/saved details
    if (
      campaignDraftData &&
      isCampaignDraftSuccess &&
      templates &&
      isTemplatesSuccess
    ) {
      const selectedTemplate = templates.find(
        (template) =>
          template?.id === `${campaignDraftData?.whatsAppDetails?.templateId}`
      );

      if (selectedTemplate) {
        // dispatch(
        //   campaignComposeActions.setSelectedTemplate({
        //     selectedTemplate
        //   })
        // );
        if (selectedTemplate?.type?.toLowerCase() !== "text") {
          dispatch(
            campaignComposeActions.setMediaDetails({
              mediaDetails: {
                addMediaFlag: true,
                mediaFileName: "",
                mediaFileSize: "",
                mediaType:
                  selectedTemplate?.type?.toLowerCase() === "image"
                    ? EMediaType.IMAGE
                    : EMediaType.DOCUMENT,
                uploadedFile: null,
              },
            })
          );
        }
      }
    }
  }, [
    campaignDraftData,
    dispatch,
    isCampaignDraftSuccess,
    isTemplatesSuccess,
    templates,
  ]);

  useEffect(() => {
    // to update sender details
    if (senderDetails && isSenderDetailsSuccess) {
      dispatch(
        campaignComposeActions.setCampaignSenderDetailsDetails({
          campaignSenderDetails: senderDetails,
        })
      );
    }
  }, [dispatch, isSenderDetailsSuccess, senderDetails]);

  useEffect(() => {
    // to update folder details
    if (folders && isFoldersSuccess) {
      dispatch(
        whatsAppCampaignSliceActions.setFolders({
          folders,
        })
      );
    }
  }, [dispatch, folders, isFoldersSuccess]);

  useEffect(() => {
    // update folders fetch status
    if (isFoldersLoading) {
      dispatch(
        whatsAppCampaignSliceActions.setFoldersStatus({
          status: "pending",
        })
      );
    }

    if (isFoldersSuccess) {
      dispatch(
        whatsAppCampaignSliceActions.setFoldersStatus({
          status: "success",
        })
      );
    }

    if (isFoldersError) {
      dispatch(
        whatsAppCampaignSliceActions.setFoldersStatus({
          status: "error",
        })
      );
    }
  }, [dispatch, isFoldersError, isFoldersLoading, isFoldersSuccess]);

  useEffect(() => {
    // update selected folder
    if (
      folders?.length &&
      (campaignDraftData?.campaignDetail?.folderId || contactableData?.folderId)
    ) {
      const folderId =
        campaignDraftData?.campaignDetail?.folderId ||
        contactableData?.folderId ||
        "";
      // update selected folder based on folderId
      const selectedFolder = folders?.find(
        (item) => item.folderId === folderId
      );

      if (selectedFolder) {
        dispatch(
          campaignSetupActions.setSelectedFolder({
            selectedFolder,
          })
        );
      }
    }
  }, [
    campaignDraftData?.campaignDetail?.folderId,
    contactableData?.folderId,
    dispatch,
    folders,
  ]);

  return { campaignDraftData, isCampaignDraftError, isCampaignDraftSuccess };
};

export default useCampaignApis;
